import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import SkeletonTableRows from './SkeletonTableRows';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';

import { useGetRacesQuery, useGetRaceQuery, useDeleteRaceMutation } from '../api/apiSlice';
import { useSportTypeLabel } from '../hooks/useSportTypeLabel';
import { formatShortDate } from '../utils/formatDate';
import RaceFormDialog from './RaceFormDialog';

const hiddenOnMobileSx = { display: { xs: 'none', sm: 'table-cell' } } as const;

interface RaceManagementProps {
    clubId?: number;
}

export default function RaceManagement({ clubId }: RaceManagementProps) {
    const { t, i18n } = useTranslation();
    const sportTypeLabel = useSportTypeLabel();
    const clubID = clubId != null ? String(clubId) : '';
    const { data: races = [], isLoading } = useGetRacesQuery(clubID, { skip: !clubID });
    const [deleteRace] = useDeleteRaceMutation();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingRaceId, setEditingRaceId] = useState<number | null>(null);
    const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);

    const { data: editingRace } = useGetRaceQuery(editingRaceId as number, { skip: editingRaceId === null });

    const handleAdd = () => {
        setEditingRaceId(null);
        setDialogOpen(true);
    };

    const handleEdit = (raceId: number) => {
        setEditingRaceId(raceId);
        setDialogOpen(true);
    };

    const handleDelete = async (raceId: number) => {
        try {
            await deleteRace(raceId).unwrap();
        } catch (error) {
            console.error('Error:', error);
            setErrorSnackbarOpen(true);
        }
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingRaceId(null);
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Box mb={2} display="flex" justifyContent="flex-end">
                <Button variant="contained" color="primary" onClick={handleAdd}>
                    {t('raceManagement.add')}
                </Button>
            </Box>
            <Paper sx={{ width: '100%', mb: 2 }}>
                <TableContainer sx={{ overflowX: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Table sx={{ minWidth: { xs: 500, sm: 650 } }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('raceManagement.title')}</TableCell>
                                <TableCell>{t('raceManagement.sport')}</TableCell>
                                <TableCell>{t('raceManagement.date')}</TableCell>
                                <TableCell sx={hiddenOnMobileSx}>{t('raceManagement.location')}</TableCell>
                                <TableCell align="right">{t('common.actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading && (
                                <SkeletonTableRows
                                    rows={2}
                                    cells={[
                                        {}, {}, {},
                                        { cellProps: { sx: hiddenOnMobileSx } },
                                        { variant: 'circular', width: 24, height: 24 },
                                    ]}
                                />
                            )}

                            {!isLoading && races.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                        {t('races.empty')}
                                    </TableCell>
                                </TableRow>
                            )}

                            {races.map((race) => (
                                <TableRow key={race.ID} hover>
                                    <TableCell>{race.TITLE}</TableCell>
                                    <TableCell>{sportTypeLabel(race.SPORT_TYPE)}</TableCell>
                                    <TableCell>{formatShortDate(race.DATE, i18n.language)}</TableCell>
                                    <TableCell sx={hiddenOnMobileSx}>{race.LOCATION}</TableCell>
                                    <TableCell align="right">
                                        <IconButton onClick={() => handleEdit(race.ID)} aria-label={`${t('common.edit')}: ${race.TITLE}`}>
                                            <EditIcon color="primary" />
                                        </IconButton>
                                        <IconButton onClick={() => handleDelete(race.ID)} aria-label={`${t('common.delete')}: ${race.TITLE}`}>
                                            <DeleteIcon color="error" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
            {dialogOpen && (editingRaceId === null || editingRace) && (
                <RaceFormDialog race={editingRaceId !== null ? editingRace : undefined} onClose={handleCloseDialog} />
            )}
            <Snackbar open={errorSnackbarOpen} autoHideDuration={4000} onClose={() => setErrorSnackbarOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setErrorSnackbarOpen(false)} severity="error" variant="filled">
                    {t('common.genericError')}
                </Alert>
            </Snackbar>
        </Box>
    );
}
